import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Briefcase, Library, BookOpen, Search, X, Menu, Plus, ExternalLink, Mic, Sparkles, GitGraph, LayoutGrid, PanelLeft } from "lucide-react";
import { useMobile } from "../../hooks/useMobile";
import RotatingBrandLogo from "./RotatingBrandLogo";
import NavBreadcrumb from "./NavBreadcrumb";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useEditLock } from "../../context/EditLockContext";
import { getAllTexts, createArticle, updateText, findTextById } from "../../services/workspaceService";
import TextEditor from "../workspace/TextEditor";
import WorkspaceTree from "../tree/WorkspaceTree";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
import { VERSION } from "../../config/version";
import LMStudioChatModal from "../common/LMStudioChatModal";
import { useReadingArticles } from "../../hooks/useReadingArticles";
import { useLmStudioLive } from "../../hooks/useLmStudioLive";
import { getRememberedWritingText, rememberedTextPath } from "../../utils/writingSession";
import { textReaderPath } from "../../utils/textRoutes";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const { canEdit, registerUnlockTap, tapProgress, tapGoal } = useEditLock();
  const locked = !canEdit;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [treeExpanded, setTreeExpanded] = useState(false);
  const [isQuickEditorOpen, setQuickEditorOpen] = useState(false);
  const [quickEditorAutoStartRecording, setQuickEditorAutoStartRecording] = useState(false);
  const [quickSavedTarget, setQuickSavedTarget] = useState(null);
  const [isLmChatOpen, setLmChatOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [proximamente, setProximamente] = useState(null);
  const [rememberedText, setRememberedText] = useState(() => getRememberedWritingText());

  const isMobile = useMobile();
  const { live: lmLive, checking: lmChecking } = useLmStudioLive();
  const lmChatEnabled = lmLive && !locked;
  const lmChatTitle = lmChecking
    ? "Comprobando LM Studio…"
    : lmLive
      ? "LM Studio Chat"
      : "LM Studio no disponible";
  const isArticleRoute =
    path.startsWith("/a/") ||
    path.startsWith("/notas/") ||
    path.includes("/editor");
  const isEditorRoute = path === "/editor" || path.startsWith("/editor/");
  const isHome = path === "/" || path === "/hoy";
  const canReturnToText = Boolean(rememberedText?.id && (path.includes("/editor") || path === "/corkboard"));
  const canOpenCorkboardFromText = Boolean(rememberedText?.id && (path.includes("/editor") || path.startsWith("/a/") || path.startsWith("/notas/")));

  useEffect(() => {
    const refreshRememberedText = () => setRememberedText(getRememberedWritingText());
    refreshRememberedText();
    window.addEventListener("focus", refreshRememberedText);
    window.addEventListener("trufapp:writing-text", refreshRememberedText);
    return () => {
      window.removeEventListener("focus", refreshRememberedText);
      window.removeEventListener("trufapp:writing-text", refreshRememberedText);
    };
  }, [path]);

  // Tree stays closed unless user opens it (no auto-expand on desktop)
  useEffect(() => {
    if (isMobile) setTreeExpanded(false);
  }, [isMobile]);

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  useWorkspace();
  const { counts: readingCounts } = useReadingArticles();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setIsSearching(true);
      }
      if (e.key === 'Escape') {
        setIsSearching(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const all = await getAllTexts();
        if (!searchQuery.trim()) {
          setSearchResults(all.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)).slice(0, 5));
          return;
        }
        const filtered = all.filter(t =>
          (t.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (t.content || "").toLowerCase().includes(searchQuery.toLowerCase())
        ).slice(0, 8);
        setSearchResults(filtered);
      } catch (e) {
        console.error(`[Search] Error:`, e);
      }
    };

    if (isSearching || searchQuery) {
      const delayDebounce = setTimeout(fetchResults, 300);
      return () => clearTimeout(delayDebounce);
    }
  }, [searchQuery, isSearching]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearching(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectResult = (art) => {
    setSearchQuery("");
    setIsSearching(false);
    navigate(textReaderPath(art));
  };

  const showProximamente = useCallback((title, description) => {
    setProximamente({ title, description });
    setTimeout(() => setProximamente(null), 4500);
  }, []);

  const safeNavigate = useCallback((to, options) => {
    try {
      navigate(to, options);
    } catch (err) {
      console.error("[Layout] navigation failed:", err);
      showProximamente("Próximamente", "Esta sección aún no está disponible.");
    }
  }, [navigate, showProximamente]);

  const handleSaveQuickNote = async (title, content, img, tags) => {
    if (locked) return;
    try {
      let saved;

      if (quickSavedTarget?.id) {
        await updateText(
          quickSavedTarget.deskId,
          quickSavedTarget.drawerId,
          quickSavedTarget.notebookId,
          quickSavedTarget.id,
          { title, content, img, tags },
        );
        saved = await findTextById(quickSavedTarget.id);
        if (!saved) {
          throw new Error("El guardado respondió OK pero el artículo no pudo volver a encontrarse.");
        }
      } else {
        saved = await createArticle(title, content, img, { tags });
      }

      setQuickSavedTarget(saved);
      setToast({
        message: `Guardado ✓ · ${saved.deskName} > ${saved.drawerName} > ${saved.notebookName}`,
        url: textReaderPath(saved),
      });
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      console.error("[Layout] quick save failed:", err);
      setToast({ message: err?.message || "Error al guardar la nota", url: null });
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  };

  const pillBtn = (isActive) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "10px",
    borderRadius: "50%",
    color: isActive ? "#fff" : "rgba(255,255,255,0.35)",
    background: isActive ? "rgba(255,255,255,0.1)" : "transparent",
    transition: "all 0.2s",
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    minWidth: isMobile ? "44px" : "auto",
    minHeight: isMobile ? "44px" : "auto",
  });

  const pillActionBtn = (bg, color) => ({
    width: isMobile ? "48px" : "44px",
    height: isMobile ? "48px" : "44px",
    borderRadius: "24px",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: bg,
    color: color,
    minWidth: "44px",
    minHeight: "44px",
  });

  return (
    <div style={styles.container}>
      {!isEditorRoute && (
      <header style={styles.header}>
        <div style={styles.leftSection}>
          {!isHome && (
            <button
              onClick={() => setTreeExpanded((v) => !v)}
              style={{ background: "none", border: "none", color: "#F9F6EE", cursor: "pointer", padding: "8px", marginLeft: "-8px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px" }}
              aria-label={treeExpanded ? "Ocultar explorador" : "Abrir explorador"}
              title={treeExpanded ? "Ocultar explorador" : "Abrir explorador"}
            >
              {isMobile ? <Menu size={22} /> : <PanelLeft size={20} />}
            </button>
          )}
          <RotatingBrandLogo version={VERSION} />
          <NavBreadcrumb />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {canReturnToText && (
            <button
              onClick={() => safeNavigate(rememberedTextPath(rememberedText))}
              style={{ background: "none", border: "none", color: "#D4AF37", cursor: "pointer", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px", flexShrink: 0 }}
              aria-label="Volver al texto"
              title={`Volver a ${rememberedText.title || "texto"}`}
            >
              <BookOpen size={21} />
            </button>
          )}

          {canOpenCorkboardFromText && (
            <button
              onClick={() => navigate("/corkboard", { state: { returnToText: rememberedText } })}
              style={{ background: "none", border: "none", color: "#A89F91", cursor: "pointer", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px", flexShrink: 0 }}
              aria-label="Abrir tablero"
              title="Abrir tablero"
            >
              <LayoutGrid size={21} />
            </button>
          )}

          <button
            onClick={() => {
              setIsSearching(true);
              setTimeout(() => searchInputRef.current?.focus(), 50);
            }}
            style={{ background: "none", border: "none", color: "#A89F91", cursor: "pointer", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px", flexShrink: 0 }}
            aria-label="Buscar (Ctrl+K)"
          >
            <Search size={22} />
          </button>

          {isMobile && (
            <button
              onClick={() => {
                if (!lmChatEnabled) return;
                setLmChatOpen(true);
              }}
              disabled={!lmChatEnabled}
              style={{
                background: "none",
                border: "none",
                color: lmLive ? "#A89F91" : "#666",
                cursor: lmChatEnabled ? "pointer" : "not-allowed",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "44px",
                minHeight: "44px",
                flexShrink: 0,
                opacity: lmChatEnabled ? 1 : 0.35,
                filter: lmChatEnabled ? "none" : "grayscale(1)",
              }}
              aria-label={lmChatTitle}
              title={lmChatTitle}
            >
              <Sparkles size={22} />
            </button>
          )}
        </div>
      </header>
      )}

      {/* Search overlay */}
      {isSearching && (
        <div ref={searchRef} style={styles.mobileSearchOverlay}>
          <div style={styles.mobileSearchHeader}>
            <div style={{ ...styles.searchBox, flex: 1, maxWidth: "100%" }}>
              <Search size={18} color="rgba(255,255,255,0.5)" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ ...styles.searchInput, fontSize: "1rem" }}
                autoFocus
              />
              {searchQuery && <X size={18} color="#888" onClick={() => setSearchQuery("")} style={{ cursor: "pointer" }} />}
            </div>
            <button
              onClick={() => { setIsSearching(false); setSearchQuery(""); }}
              style={{ background: "none", border: "none", color: "#A89F91", cursor: "pointer", padding: "8px 12px", fontSize: "0.9rem" }}
            >
              Cancelar
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
            {searchResults.map(art => (
              <div key={art.id} style={styles.resultItem} onClick={() => handleSelectResult(art)}>
                <div style={styles.resultMeta}>{art.notebookName}</div>
                <div style={styles.resultTitle}>{art.title}</div>
              </div>
            ))}
            {searchResults.length === 0 && searchQuery.trim() && (
              <div style={{ padding: "20px", textAlign: "center", color: "#666", fontSize: "0.9rem" }}>Sin resultados</div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
        {/* Desktop tree — hidden on Hoy so capture uses full width */}
        {!isMobile && !isHome && !isEditorRoute && (
          <WorkspaceTree collapsed={!treeExpanded} onToggleCollapse={(c) => setTreeExpanded(!c)} />
        )}

        {/* Mobile tree overlay */}
        {isMobile && treeExpanded && (
          <>
            <div
              style={styles.mobileBackdrop}
              onClick={() => setTreeExpanded(false)}
            />
            <div style={styles.mobileSidebar}>
              <WorkspaceTree collapsed={false} onToggleCollapse={(c) => setTreeExpanded(!c)} />
            </div>
          </>
        )}

        <main style={{
          ...styles.main,
          overflowY: isEditorRoute ? "hidden" : "auto",
          paddingBottom: isMobile
            ? (isArticleRoute ? "16px" : "100px")
            : "24px",
          ...(isHome ? { display: "flex", flexDirection: "column", alignItems: "center" } : {}),
          ...(isEditorRoute ? { flex: 1, minHeight: 0, paddingBottom: 0, display: "flex", flexDirection: "column" } : {}),
        }}>
          <ErrorBoundary key={path}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Unified Master Glass Pill - Navigation & Actions */}
      {!isArticleRoute && (
        <div style={{ ...styles.pillContainer, bottom: isMobile ? `max(16px, env(safe-area-inset-bottom))` : "24px" }}>
          <nav style={{ ...styles.masterPill, gap: isMobile ? "6px" : "12px", padding: isMobile ? "4px 8px" : "6px 12px" }}>
            <Link to="/" style={pillBtn(path === "/" || path === "/hoy")} className="tap-target" title="Hoy — captura del día">
              <Home size={isMobile ? 22 : 24} />
            </Link>

            <Link to="/read" style={{ ...pillBtn(path === "/read"), position: "relative" }} className="tap-target" title={`Lectura — ${readingCounts.unread} por leer`}>
              <BookOpen size={isMobile ? 22 : 24} />
              {readingCounts.unread > 0 && (
                <span style={styles.readBadge} aria-label={`${readingCounts.unread} por leer`}>
                  {readingCounts.unread > 99 ? "99+" : readingCounts.unread}
                </span>
              )}
            </Link>

            <Link to="/library" style={pillBtn(path === "/library")} className="tap-target" title="Biblioteca — todos los artículos">
              <Library size={isMobile ? 22 : 24} />
            </Link>

            <Link to="/github" style={pillBtn(path === "/github")} className="tap-target" title="GitHub — repos y archivos">
              <GitGraph size={isMobile ? 22 : 24} />
            </Link>

            <div style={styles.pillSeparator} />

            <button
              onClick={() => {
                if (locked) return;
                setQuickSavedTarget(null);
                setQuickEditorAutoStartRecording(true);
                setQuickEditorOpen(true);
              }}
              style={{
                ...pillActionBtn("rgba(212,175,55,0.15)", "#D4AF37"),
                opacity: locked ? 0.35 : 1,
                filter: locked ? "grayscale(1)" : "none",
                cursor: locked ? "not-allowed" : "pointer",
              }}
              title="Notas de voz — subir audios o dictar"
              className="tap-target"
              disabled={locked}
            >
              <Mic size={isMobile ? 24 : 26} />
            </button>

            <button
              onClick={() => {
                if (locked) {
                  const wasLast = tapProgress + 1 >= tapGoal;
                  registerUnlockTap();
                  if (wasLast) {
                    window.setTimeout(() => {
                      setQuickSavedTarget(null);
                      setQuickEditorAutoStartRecording(false);
                      setQuickEditorOpen(true);
                    }, 0);
                    return;
                  }
                  const left = tapGoal - tapProgress - 1;
                  setToast({
                    message: `Bloqueado — ${left} toques más (ícono o +)`,
                    url: null,
                  });
                  setTimeout(() => setToast(null), 3500);
                  return;
                }
                setQuickSavedTarget(null);
                setQuickEditorAutoStartRecording(false);
                setQuickEditorOpen(true);
              }}
              style={{
                ...pillActionBtn("rgba(255,255,255,0.1)", "#fff"),
                opacity: locked ? 0.55 : 1,
                filter: locked ? "grayscale(0.6)" : "none",
                cursor: "pointer",
              }}
              title={locked ? "Tocá 5 veces para escribir" : "Escribir"}
              className="tap-target"
            >
              <Plus size={isMobile ? 24 : 26} />
            </button>

            {!isMobile && (
              <button
                onClick={() => {
                  if (!lmChatEnabled) return;
                  setLmChatOpen(true);
                }}
                style={{
                  ...pillActionBtn("rgba(167,139,250,0.15)", "#A78BFA"),
                  opacity: lmChatEnabled ? 1 : 0.35,
                  filter: lmChatEnabled ? "none" : "grayscale(1)",
                  cursor: lmChatEnabled ? "pointer" : "not-allowed",
                }}
                title={lmChatTitle}
                className="tap-target"
                disabled={!lmChatEnabled}
              >
                <Sparkles size={isMobile ? 24 : 26} />
              </button>
            )}

            <div style={styles.pillSeparator} />

            <Link to="/drawers" style={pillBtn(path.includes("drawers"))} className="tap-target" title="Cajones — organizar cuadernos">
              <Briefcase size={isMobile ? 22 : 24} />
            </Link>

            <Link to="/corkboard" style={pillBtn(path === "/corkboard" || path.startsWith("/corkboard"))} className="tap-target" title="Corcho — fichas libres">
              <LayoutGrid size={isMobile ? 22 : 24} />
            </Link>

            <div style={{ position: 'relative' }}>
              <button onClick={() => setLinksOpen(!linksOpen)} style={{ ...pillBtn(false), background: 'transparent', border: 'none' }} className="tap-target">
                <ExternalLink size={isMobile ? 20 : 22} />
              </button>
              {linksOpen && (
                <div style={styles.linksDropdown}>
                  <a href="https://catreader.gatrivi.com" target="_blank" rel="noopener noreferrer" style={styles.linkItem}>CatReader</a>
                  <a href="https://cathedral.gatrivi.com" target="_blank" rel="noopener noreferrer" style={styles.linkItem}>Cathedral</a>
                  <a href="https://devtrivi.gatrivi.com" target="_blank" rel="noopener noreferrer" style={styles.linkItem}>DevTrivi</a>
                  <a href="https://tmm.gatrivi.com" target="_blank" rel="noopener noreferrer" style={styles.linkItem}>TMM</a>
                  <Link to="/dashboard" onClick={() => setLinksOpen(false)} style={styles.linkItem}>Panel</Link>
                  <Link to="/kanban" onClick={() => setLinksOpen(false)} style={styles.linkItem}>Kanban</Link>
                  <Link to="/atlas" onClick={() => setLinksOpen(false)} style={styles.linkItem}>Atlas</Link>
                  <Link to="/help" onClick={() => setLinksOpen(false)} style={styles.linkItem}>Ayuda</Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}

      <TextEditor
        isOpen={isQuickEditorOpen}
        onClose={() => {
          setQuickEditorOpen(false);
          setQuickEditorAutoStartRecording(false);
          setQuickSavedTarget(null);
        }}
        onSave={handleSaveQuickNote}
        mode="create"
        autoStartRecording={quickEditorAutoStartRecording}
      />

      <LMStudioChatModal isOpen={isLmChatOpen} onClose={() => setLmChatOpen(false)} />

      {toast && (
        <div
          onClick={() => {
            if (toast.url) {
              safeNavigate(toast.url);
            }
            setToast(null);
          }}
          style={styles.toast}
        >
          <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>{toast.message}</div>
          {toast.url && (
            <div style={{ fontSize: "0.7rem", opacity: 0.7, marginTop: "2px" }}>Toca para ver el manuscrito</div>
          )}
        </div>
      )}

      {proximamente && (
        <div style={styles.proximamenteToast} role="status">
          <div style={{ fontWeight: "bold", fontSize: "0.85rem", color: "#D4AF37" }}>
            Próximamente — {proximamente.title}
          </div>
          <div style={{ fontSize: "0.75rem", opacity: 0.85, marginTop: "4px", lineHeight: 1.4 }}>
            {proximamente.description}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "transparent",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 16px",
    backgroundColor: "rgba(20, 20, 20, 0.4)",
    backdropFilter: "blur(24px)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    position: "sticky",
    top: 0,
    zIndex: 110,
  },
  leftSection: { display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 },
  searchBox: { display: "flex", alignItems: "center", gap: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "20px", padding: "4px 12px" },
  searchInput: { background: "transparent", border: "none", color: "#fff", fontSize: "0.8rem", outline: "none", width: "100%" },

  mobileSearchOverlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(10,10,10,0.98)",
    zIndex: 300,
    display: "flex",
    flexDirection: "column",
    paddingTop: "env(safe-area-inset-top)",
  },
  mobileSearchHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
  },

  mobileBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 40,
  },
  mobileSidebar: {
    position: "fixed",
    top: 0, left: 0, bottom: 0,
    width: "280px",
    zIndex: 50,
    transform: "translateX(0)",
    transition: "transform 0.3s ease",
  },

  pillContainer: {
    position: "fixed",
    left: 0, right: 0,
    display: "flex",
    justifyContent: "center",
    padding: "0 20px",
    zIndex: 1000,
    pointerEvents: "none",
  },
  masterPill: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    background: "rgba(20, 20, 20, 0.6)",
    backdropFilter: "blur(32px)",
    borderRadius: "40px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
    pointerEvents: "auto",
    overflowX: "auto",
    maxWidth: "calc(100vw - 20px)",
  },
  readBadge: {
    position: "absolute",
    top: "-4px",
    right: "-6px",
    minWidth: "16px",
    height: "16px",
    padding: "0 4px",
    borderRadius: "8px",
    background: "#4ade80",
    color: "#0a1a0f",
    fontSize: "0.6rem",
    fontWeight: 800,
    lineHeight: "16px",
    textAlign: "center",
    pointerEvents: "none",
  },
  pillActionBtn: {
    width: "44px",
    height: "44px",
    borderRadius: "22px",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  pillSeparator: {
    width: "1px",
    height: "20px",
    background: "rgba(255,255,255,0.1)",
    margin: "0 2px",
  },
  main: { flex: 1, minWidth: 0, width: "100%", position: "relative", zIndex: 10 },
  resultsDropdown: { position: "absolute", top: "100%", left: 0, right: 0, background: "rgba(20, 20, 20, 0.95)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", marginTop: "8px", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", zIndex: 200, maxHeight: "400px", overflowY: "auto" },
  resultItem: { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer" },
  resultTitle: { color: "#fff", fontSize: "0.85rem", fontWeight: "600" },
  resultMeta: { color: "rgba(255,255,255,0.4)", fontSize: "0.6rem", textTransform: "uppercase", marginBottom: "2px" },
  linksDropdown: { position: "absolute", bottom: "60px", right: 0, background: "rgba(20, 20, 20, 0.95)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", boxShadow: "0 -10px 30px rgba(0,0,0,0.5)", zIndex: 200, minWidth: "140px", display: "flex", flexDirection: "column", padding: "6px 0" },
  linkItem: { padding: "8px 14px", color: "#fff", textDecoration: "none", fontSize: "0.8rem", fontWeight: "500", transition: "background 0.2s", display: "block" },
  toast: {
    position: "fixed",
    top: "80px",
    right: "20px",
    background: "rgba(212, 175, 55, 0.9)",
    color: "#1A100C",
    padding: "12px 20px",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
    zIndex: 2000,
    cursor: "pointer",
    animation: "fadeIn 0.3s ease-out",
    maxWidth: "300px",
  },
  proximamenteToast: {
    position: "fixed",
    bottom: "max(100px, calc(16px + env(safe-area-inset-bottom) + 72px))",
    left: "20px",
    right: "20px",
    margin: "0 auto",
    maxWidth: "360px",
    background: "rgba(26, 16, 12, 0.95)",
    color: "#F9F6EE",
    padding: "14px 18px",
    borderRadius: "12px",
    border: "1px solid rgba(212, 175, 55,0.35)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
    zIndex: 2001,
    animation: "fadeIn 0.3s ease-out",
    textAlign: "center",
  },
};